import type { PointCoordinate } from "../types";

/**
 * Calcula el área de un polígono usando la fórmula de Shoelace en coordenadas esféricas
 * @param points - Array de puntos con latitud y longitud
 * @returns Área en metros cuadrados
 */
export const calculatePolygonArea = (points: PointCoordinate[]): number => {
  if (points.length < 3) return 0;

  const earthRadius = 6371000; // Radio de la Tierra en metros

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const lat1 = (points[i].latitude * Math.PI) / 180;
    const lat2 = (points[j].latitude * Math.PI) / 180;
    const lon1 = (points[i].longitude * Math.PI) / 180;
    const lon2 = (points[j].longitude * Math.PI) / 180;

    area += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  area = Math.abs((area * earthRadius * earthRadius) / 2);
  return area;
};

/**
 * Calcula la distancia de una ruta lineal usando la fórmula de Haversine
 * @param points - Array de puntos con latitud y longitud
 * @returns Distancia total en metros
 */
export const calculateLinearDistance = (points: PointCoordinate[]): number => {
  if (points.length < 2) return 0;

  const earthRadius = 6371000; // Radio de la Tierra en metros
  let totalDistance = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const point1 = points[i];
    const point2 = points[i + 1];

    const lat1Rad = (point1.latitude * Math.PI) / 180;
    const lat2Rad = (point2.latitude * Math.PI) / 180;
    const deltaLatRad = ((point2.latitude - point1.latitude) * Math.PI) / 180;
    const deltaLonRad = ((point2.longitude - point1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
      Math.cos(lat1Rad) *
        Math.cos(lat2Rad) *
        Math.sin(deltaLonRad / 2) *
        Math.sin(deltaLonRad / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = earthRadius * c;

    totalDistance += distance;
  }

  return totalDistance;
};

/**
 * Calcula el tiempo estimado de ruta basado en velocidad promedio
 * @param distanceKm - Distancia en kilómetros
 * @param avgSpeedKmh - Velocidad promedio en km/h (default: 50)
 * @returns String formateado con el tiempo estimado
 */
export const calculateEstimatedTime = (
  distanceKm: number,
  avgSpeedKmh: number = 50
): string => {
  const timeInHours = distanceKm / avgSpeedKmh;
  const timeInMinutes = Math.round(timeInHours * 60);

  if (timeInMinutes < 60) {
    return `${timeInMinutes} min`;
  } else {
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    return `${hours}h ${minutes}min`;
  }
};
